import { useState, useEffect } from 'react';
import { FeedbackState } from './types';
import { ThumbsDownIcon, ThumbsUpIcon } from './icons';
import FeedbackButton from './FeedbackButton';
import { ACTIVITY_DETAILS_COLORS, ACTIVITY_DETAILS_RESPONSIVE, ACTIVITY_DETAILS_TYPOGRAPHY } from './constants';
import { api } from '../../services/api';
import type { FeedbackType } from '../../types/activity';
import { useAnalytics } from '../../contexts/AnalyticsContext';

interface FeedbackSectionProps {
  activityId: string;
  initialFeedback?: 'LIKE' | 'DISLIKE' | null;
  onFeedbackUpdate?: (statistics: { thumbsUpCount: number; thumbsDownCount: number }) => void;
}

/**
 * FeedbackSection Component
 * 
 * "Was this activity helpful?" feedback section with buttons.
 * Receives initial feedback state from parent and allows submission.
 * 
 * Design specs:
 * - Container: flex column, gap 16px
 * - Responsive padding and typography
 * 
 * Title:
 * - Text: "Was this activity helpful?"
 * - Font: DM Sans Bold, responsive 18px→20px→22px
 * - Letter spacing: -0.72px
 * - Color: #1B2831
 * 
 * Buttons container: flex row, gap 8px
 * 
 * Thank you message:
 * - Font: DM Sans Medium, responsive 12px→13px→14px
 * - Color: #4D5762
 * - Opacity: 0 by default, 1 after selection
 */
export default function FeedbackSection({ activityId, initialFeedback, onFeedbackUpdate }: FeedbackSectionProps) {
  const { track } = useAnalytics();
  // Initialize state from prop
  const [feedback, setFeedback] = useState<FeedbackState>({
    submitted: initialFeedback !== undefined && initialFeedback !== null,
    type: initialFeedback === 'LIKE' ? 'positive' : initialFeedback === 'DISLIKE' ? 'negative' : null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update local state when initialFeedback prop changes
  useEffect(() => {
    if (initialFeedback !== undefined) {
      setFeedback({
        submitted: initialFeedback !== null,
        type: initialFeedback === 'LIKE' ? 'positive' : initialFeedback === 'DISLIKE' ? 'negative' : null,
      });
    }
  }, [initialFeedback]);

  const handleFeedback = async (type: 'positive' | 'negative') => {
    const feedbackType: FeedbackType = type === 'positive' ? 'LIKE' : 'DISLIKE';

    try {
      setIsSubmitting(true);
      setError(null);

      const result = await api.submitFeedback(activityId, feedbackType);

      track('activity_feedback', { activity_id: String(activityId), feedback_type: feedbackType });

      // Update local state
      setFeedback({ submitted: true, type });

      // Notify parent of statistics update
      onFeedbackUpdate?.(result.statistics);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
      // Don't update local state on error - keep previous selection
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`flex flex-col gap-4 ${ACTIVITY_DETAILS_RESPONSIVE.horizontalMargin} mt-6`}
    >
      {/* Title */}
      <h3
        className={`font-bold ${ACTIVITY_DETAILS_TYPOGRAPHY.feedbackTitle}`}
        style={{
          fontFamily: 'DM Sans, sans-serif',
          letterSpacing: '-0.72px',
          color: ACTIVITY_DETAILS_COLORS.textPrimary,
          margin: 0,
          lineHeight: 1,
        }}
      >
        Was this activity helpful?
      </h3>

      {/* Feedback Buttons */}
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <FeedbackButton
            icon={<ThumbsUpIcon size={24} color={ACTIVITY_DETAILS_COLORS.textPrimary} />}
            label="It was great!"
            onClick={() => handleFeedback('positive')}
            isSelected={feedback.type === 'positive'}
            disabled={isSubmitting}
          />
          <FeedbackButton
            icon={<ThumbsDownIcon size={24} color={ACTIVITY_DETAILS_COLORS.textPrimary} />}
            label="Didn't work"
            onClick={() => handleFeedback('negative')}
            isSelected={feedback.type === 'negative'}
            disabled={isSubmitting}
          />
        </div>

        {/* Error Message */}
        {error && (
          <p
            className={`font-medium ${ACTIVITY_DETAILS_TYPOGRAPHY.thankYouMessage}`}
            style={{
              fontFamily: 'DM Sans, sans-serif',
              color: '#DC2626',
              margin: 0,
            }}
          >
            {error}
          </p>
        )}

        {/* Thank You Message */}
        <p
          className={`font-medium pb-4 ${ACTIVITY_DETAILS_TYPOGRAPHY.thankYouMessage}`}
          style={{
            fontFamily: 'DM Sans, sans-serif',
            color: ACTIVITY_DETAILS_COLORS.textSecondary,
            opacity: feedback.submitted && !error ? 1 : 0,
            transition: 'opacity 0.3s ease',
            margin: 0,
          }}
        >
          Thank you! Your feedback helps us improve recommendations
        </p>
      </div>
    </div>
  );
}
